import { useEffect, useState } from "react";
import { useDemo } from "../zustand/DemoZustand";
import { Abschnitt, Punkt } from "../bausteine/Grundelemente";
import Reiter, { Tafel } from "../bausteine/Reiter";
import Werkzeugkreis, { WERKZEUGE } from "../bausteine/Werkzeugkreis";
import Zitatziel from "../bausteine/Zitatziel";
import Notizfeld from "../bausteine/Notizfeld";
import Notizliste from "../bausteine/Notizliste";
import Farbwahl from "../bausteine/Markerfarben";
import Meinungsbild, { Fragenliste } from "../bausteine/Meinungsbild";
import { Tagesordnungsliste } from "../bausteine/Sitzungsteile";
import Vorlagenblatt from "./Dokument";
import Fraktionschat from "./Fraktionschat";
import {
  Dokument,
  Kalender,
  Schloss,
  Sprechblase,
  Textmarker,
  Uhr,
  Waage,
} from "../bausteine/Symbole";
import {
  notizenZuDatei,
  fragenZuTop,
  kanalZuTop,
  offenePunkte,
  standDesTop,
} from "../hilfen/auswertung";
import { useChat } from "../zustand/ChatZustand";
import { fristInWorten, langesDatum, uhrzeit } from "../hilfen/datum";

/*
  Der Sitzungsraum — die rechte Spalte.

  Er löst vier getrennte Ansichten ab. Vorher musste man sich vom
  Tagesordnungspunkt in die Vorlage klicken, von dort zurück und in den Chat,
  und wieder zurück fürs Meinungsbild. Auf einem Tablet mit dem Finger war das
  der Grund, warum Leute mittendrin aufgaben.

  Jetzt liegen die drei Dinge, die man an einem Punkt tut, nebeneinander:
  lesen, besprechen, Position beziehen. Ein Fingertipp dazwischen, kein Zurück.

  Ohne gewählten Punkt steht hier die Sitzungsübersicht — die Leitfrage des
  Lastenhefts, „Was muss ich bis zur nächsten Sitzung tun?“, bleibt damit die
  erste Antwort, die man bekommt.
*/

const REITER = ["vorlage", "notizen", "chat", "meinungsbild"];

export default function Sitzungsraum({ topId, reiter, zitat, mitKontext, oeffne }) {
  const { stammdaten, zustand, ich, aktionen } = useDemo();
  const chat = useChat();
  const top = topId ? stammdaten.tagesordnung.find((t) => t.id === topId) : null;

  /*
    Welcher Reiter offen ist, gehört in diese Ansicht — nicht in den Verlauf
    der Anwendung. Sonst wäre jeder Reiterwechsel ein Schritt, den die
    Zurück-Taste rückgängig machen müsste. Von außen kommt nur der Wunsch,
    wo man landen möchte.
  */
  const [offenerReiter, setzeOffenenReiter] = useState(reiter || "vorlage");

  /* Welche Fassung gerade gelesen wird — die alte ist an keinem Punkt mehr. */
  const [gezeigteDatei, setzeGezeigteDatei] = useState(null);

  /* Der Weg des Zitats zwischen Vorlage und Notizen. */
  const [zitatEntwurf, setzeZitatEntwurf] = useState(zitat || null);
  const [springeZu, setzeSpringeZu] = useState(null);

  /* Auf schmalen Geräten steht die Tagesordnung nicht daneben, sondern hier. */
  const [tagesordnungOffen, setzeTagesordnungOffen] = useState(false);

  /* Der Werkzeugkreis unten rechts. */
  const [werkzeugeOffen, setzeWerkzeugeOffen] = useState(false);
  const [werkzeugHinweis, setzeWerkzeugHinweis] = useState(null);
  const [leisteOffen, setzeLeisteOffen] = useState(false);

  /*
    Das laufende Werkzeug. Solange keines an ist, bleibt eine Textauswahl
    folgenlos — man kann lesen und kopieren, ohne gefragt zu werden.
  */
  const [modus, setzeModus] = useState(null);
  const [sichtbarkeit, setzeSichtbarkeit] = useState("fraktion");
  const [farbe, setzeFarbe] = useState("gelb");

  /* Die angetippte Markierung, über die gerade entschieden wird. */
  const [gewaehlt, setzeGewaehlt] = useState(null);

  /* Die Markierung, an die gerade etwas an den Rand geschrieben wird. */
  const [notizEntwurf, setzeNotizEntwurf] = useState(null);

  /* Punkt oder gewünschter Reiter geändert: von vorn. */
  useEffect(() => {
    setzeOffenenReiter(reiter && REITER.includes(reiter) ? reiter : "vorlage");
    setzeGezeigteDatei(null);
    /* Ein Zitat, das von einem anderen Punkt herübergereicht wurde, bleibt. */
    setzeZitatEntwurf(zitat || null);
    setzeSpringeZu(null);
    setzeTagesordnungOffen(false);
    setzeWerkzeugeOffen(false);
    setzeWerkzeugHinweis(null);
    setzeModus(null);
    setzeGewaehlt(null);
    setzeNotizEntwurf(null);
  }, [topId, reiter, zitat]);

  if (!topId) return <Sitzungsuebersicht oeffne={oeffne} mitKontext={mitKontext} />;

  if (!top) {
    return (
      <div className="karte" style={{ marginTop: "1.5rem" }}>
        <p>Dieser Tagesordnungspunkt wurde nicht gefunden.</p>
      </div>
    );
  }

  const stand = standDesTop(top, zustand, stammdaten, ich.id);
  const fragen = fragenZuTop(stammdaten, top.id);
  const beitraege = chat.nachrichten.filter((n) => n.topId === top.id);
  const sitzung = stammdaten.sitzungen[top.sitzungId];

  const dateiId = gezeigteDatei || (stand.datei ? stand.datei.id : null);

  const notizen = notizenZuDatei(zustand, ich.id, dateiId);

  /*
    Der Punkt hat keinen eigenen Chat mehr. Besprochen wird er im Kanal des
    Gremiums, das über ihn berät — dort steht er neben dem, was vor und nach
    dieser Sitzung zur selben Sache gesagt wurde.
  */
  const kanalDesTop = kanalZuTop(stammdaten, top);
  const kanalName =
    ((stammdaten.kanaele || []).find((k) => k.id === kanalDesTop) || {}).name || "Fraktion";

  /*
    Vier Reiter statt drei. „Notizen“ hieß bisher so, enthielt aber den
    Chat — die beiden werden hier getrennt, weil sie verschiedene Vorgänge
    sind: Am Dokument wird angemerkt, mit Menschen wird geredet. Der Chat
    zieht später zu Matrix um; der Reiter bleibt dann, sein Inhalt wechselt.
  */
  const reiterListe = [
    { schluessel: "vorlage", titel: "Vorlage", symbol: Dokument },
    {
      schluessel: "notizen",
      titel: "Notizen",
      symbol: Textmarker,
      anzahl: notizen.length,
    },
    {
      schluessel: "chat",
      titel: "Chat",
      symbol: Sprechblase,
      anzahl: beitraege.length + fragen.length,
    },
    { schluessel: "meinungsbild", titel: "Meinungsbild", symbol: Waage },
  ];

  /*
    Ein Werkzeug wurde gewählt. Markieren und Zitieren sind Zustände, keine
    einmaligen Befehle: Sie bleiben an, bis man sie ausschaltet, damit man
    mehrere Stellen hintereinander bearbeiten kann, ohne jedes Mal wieder in
    den Kreis zu greifen.
  */
  function werkzeugGewaehlt(schluessel) {
    const werkzeug = WERKZEUGE.find((w) => w.schluessel === schluessel);
    if (!werkzeug) return;

    if (werkzeug.modus) {
      setzeOffenenReiter("vorlage");
      setzeModus((bisher) => (bisher === schluessel ? null : schluessel));
      setzeWerkzeugHinweis(null);
      return;
    }

    setzeWerkzeugHinweis(werkzeug);
  }

  /*
    Eine Markierung wurde angetippt. Zusammengesucht wird, was das Pop-up
    braucht: das Zitat, ob die Stelle bisher nur privat war und ob sie in der
    Fraktion schon besprochen wird.
  */
  function markierungAngetippt({ ids, stelle }) {
    const treffer = zustand.annotationen.filter((a) => ids.includes(a.id));
    if (!treffer.length) return;
    const a = treffer[0];

    setzeGewaehlt({
      annotationId: a.id,
      zitat: a.zitat,
      stelle,
      privat: a.sichtbarkeit === "privat",
      eigen: a.verfasserId === ich.id,
      farbe: a.farbe || "gelb",
      notiz: a.notiz || "",
      bereitsZitiert: chat.nachrichten.some((n) => n.annotationId === a.id),
    });
  }

  /* Und jetzt entscheidet sich, wohin die Stelle geht. */
  function zielGewaehlt({ art, topId: zielTop }) {
    const frage = gewaehlt;
    setzeGewaehlt(null);
    if (!frage) return;

    /*
      Eine Notiz ist der einzige Weg, der die Sichtbarkeit *nicht* ändert.
      Wer sich eine private Notiz an den Rand schreibt, will genau das — eine
      private Notiz. Alles andere gibt die Stelle an die Fraktion weiter.
    */
    if (art === "notiz") {
      setzeNotizEntwurf({
        annotationId: frage.annotationId,
        zitat: frage.zitat,
        stelle: frage.stelle,
        notiz: frage.notiz,
        privat: frage.privat,
      });
      return;
    }

    /*
      Wer eine private Markierung weitergibt, macht sie damit für die Fraktion
      sichtbar — sonst stünde im Chat ein Zitat, dessen Textstelle die anderen
      im Dokument nicht finden.
    */
    if (frage.privat && art !== "markierung") {
      aktionen.macheAnnotationSichtbar(frage.annotationId);
    }

    if (art === "chat" && zielTop) {
      /*
        Ein anderer Tagesordnungspunkt: Das Zitat reist im Ansichtsparameter
        mit, weil der Sitzungsraum für den neuen Punkt frisch aufgebaut wird.
      */
      oeffne("sitzung", { topId: zielTop, reiter: "chat", zitat: frage.annotationId });
      return;
    }

    if (art === "chat") {
      setzeZitatEntwurf(frage.annotationId);
      setzeOffenenReiter("chat");
      return;
    }

    if (art === "aufgabe") {
      aktionen.fuegeAufgabeHinzu({
        titel: kuerze(frage.zitat, 70),
        beschreibung: `Aus der Vorlage zu TOP ${top.nummer}: „${frage.zitat}“`,
        topId: top.id,
        vorlageId: stand.vorlage ? stand.vorlage.id : null,
      });
      setzeWerkzeugHinweis({
        titel: "Aufgabe angelegt",
        fertig: true,
        erklaerung: "Sie steht in deiner Aufgabenliste, fällig zur Sitzung.",
      });
      return;
    }

    /* „Nur markieren“ — die Markierung steht bereits im Text. */
  }

  function markierungEntfernen() {
    if (!gewaehlt) return;
    aktionen.entferneAnnotation(gewaehlt.annotationId);
    setzeGewaehlt(null);
  }

  /*
    Die Notiz ist geschrieben. Sie landet im Reiter „Notizen“, und
    dorthin wird auch gewechselt — sonst schriebe man etwas und sähe nicht,
    wo es gelandet ist.
  */
  function notizSichern(text) {
    if (!notizEntwurf) return;
    aktionen.setzeAnnotationNotiz(notizEntwurf.annotationId, text);
    setzeNotizEntwurf(null);
    setzeOffenenReiter("notizen");
  }

  /*
    Entfernt wird nur der Text, nicht die Markierung. Aus der Notiz wird
    wieder eine bloße Markierung — die Stelle bleibt im Dokument farbig.
  */
  function notizEntfernen() {
    if (!notizEntwurf) return;
    aktionen.setzeAnnotationNotiz(notizEntwurf.annotationId, "");
    setzeNotizEntwurf(null);
  }

  /* Aus der Liste heraus eine eigene Notiz bearbeiten. */
  function notizBearbeiten(annotation) {
    setzeNotizEntwurf({
      annotationId: annotation.id,
      zitat: annotation.zitat,
      stelle: null,
      notiz: annotation.notiz || "",
      privat: annotation.sichtbarkeit === "privat",
    });
  }

  /*
    Von der Notiz in den Chat — der eine Weg, der aus dem Randbereich
    herausführt. Eine private Stelle wird dabei für die Fraktion sichtbar,
    sonst stünde im Chat ein Zitat, das die anderen im Text nicht finden.
  */
  function notizInDenChat(annotation) {
    if (annotation.sichtbarkeit === "privat") {
      aktionen.macheAnnotationSichtbar(annotation.id);
    }
    setzeZitatEntwurf(annotation.id);
    setzeOffenenReiter("chat");
  }

  /* Und zurück zur Textstelle. */
  function zurTextstelle(annotation) {
    if (annotation.dateiId !== dateiId) setzeGezeigteDatei(annotation.dateiId);
    setzeZitatEntwurf(null);
    setzeSpringeZu(annotation.id);
    setzeOffenenReiter("vorlage");
  }

  const punkte = stammdaten.tagesordnung;
  const laufendesWerkzeug = WERKZEUGE.find((w) => w.schluessel === modus);

  return (
    <>
      {/*
        Schmales Gerät: Die Tagesordnung steht nicht daneben, also muss sie von
        hier erreichbar sein — sonst käme man von einem Punkt zum nächsten nur
        über den Zurück-Knopf, und genau das sollte wegfallen.
      */}
      {!mitKontext && (
        <div className="tostreifen">
          <button
            className="tostreifen__knopf"
            aria-expanded={tagesordnungOffen}
            onClick={() => setzeTagesordnungOffen((offen) => !offen)}
          >
            <span>
              TOP {top.nummer} von {punkte.length} · Tagesordnung
            </span>
            <span aria-hidden="true">{tagesordnungOffen ? "▴" : "▾"}</span>
          </button>

          {tagesordnungOffen && (
            <div className="tostreifen__liste">
              <Tagesordnungsliste oeffne={oeffne} gewaehltId={top.id} />
            </div>
          )}
        </div>
      )}

      <div className="detailkopf">
        <p className="detailkopf__vorspann">
          Tagesordnungspunkt {top.nummer} · {langesDatum(sitzung.beginn)},{" "}
          {uhrzeit(sitzung.beginn)}
        </p>
        <h1 className="detailkopf__titel">{top.titel}</h1>

        {/*
          Nur was auffällt, steht hier. „Öffentlicher Tagesordnungspunkt“ ist
          der Normalfall und „Von dir gelesen“ steht ohnehin bei jedem Punkt,
          den man gerade offen hat — beides sind Bestätigungen ohne
          Neuigkeitswert. Die Nichtöffentlichkeit dagegen ändert, was man mit
          dem Gelesenen tun darf.
        */}
        {!top.oeffentlich && (
          <div className="detailkopf__marken">
            <span className="marke-nichtoeffentlich">
              <Schloss groesse={15} />
              Nichtöffentlich
            </span>
          </div>
        )}

        {!top.oeffentlich && top.nichtoeffentlichGrund && (
          <p className="klein" style={{ marginTop: "var(--luft-3)", color: "var(--akzent-dunkel)" }}>
            <strong>Grund der Nichtöffentlichkeit:</strong> {top.nichtoeffentlichGrund} Inhalte
            dieses Punktes gehören nicht in die Öffentlichkeit und nicht in private Chatgruppen.
          </p>
        )}
      </div>

      <Reiter reiter={reiterListe} aktiv={offenerReiter} aufWechsel={setzeOffenenReiter} />

      <Tafel schluessel="vorlage" aktiv={offenerReiter}>
        {modus && (
          <div className="modusleiste">
            <p className="modusleiste__was">
              <laufendesWerkzeug.symbol groesse={18} />
              <span>
                <strong>{laufendesWerkzeug.titel} ist an.</strong> Streich mit dem Finger über
                eine Textstelle. Antippen einer Markierung fragt, wohin sie soll.
              </span>
            </p>

            {modus === "markieren" && (
              <Farbwahl gewaehlt={farbe} aufWahl={setzeFarbe} beschriftung="Farbe des Markers" />
            )}

            {modus === "markieren" && (
              <div className="wahl wahl--eng">
                {[
                  ["fraktion", "Für die Fraktion"],
                  ["privat", "Nur für mich"],
                ].map(([wert, beschriftung]) => (
                  <button
                    key={wert}
                    className="wahl__knopf"
                    aria-pressed={sichtbarkeit === wert}
                    onClick={() => setzeSichtbarkeit(wert)}
                  >
                    {beschriftung}
                  </button>
                ))}
              </div>
            )}

            <button className="knopf knopf--zurueckhaltend" onClick={() => setzeModus(null)}>
              Fertig
            </button>
          </div>
        )}

        {dateiId ? (
          <Vorlagenblatt
            dateiId={dateiId}
            springeZu={springeZu}
            aufFassung={setzeGezeigteDatei}
            aufLeiste={setzeLeisteOffen}
            modus={modus}
            sichtbarkeit={sichtbarkeit}
            farbe={farbe}
            aufMarkierung={markierungAngetippt}
          />
        ) : (
          <div className="karte">
            <p className="leise">
              {stand.vorlage
                ? "Die Verwaltung stellt zu dieser Vorlage keine Unterlagen bereit."
                : "Zu diesem Punkt liegt keine Vorlage vor."}
            </p>
          </div>
        )}
      </Tafel>

      <Tafel schluessel="notizen" aktiv={offenerReiter}>
        <Notizliste
          dateiId={dateiId}
          aufTextstelle={zurTextstelle}
          aufInDenChat={notizInDenChat}
          aufBearbeiten={notizBearbeiten}
        />
      </Tafel>

      <Tafel schluessel="chat" aktiv={offenerReiter}>
        <Fragenliste fragen={fragen} />

        {/*
          Die Rückansicht: Beiträge aus dem Kanal des Gremiums, die auf diesen
          Punkt verweisen. Kein eigener Kanal — derselbe Beitrag steht auch im
          Kanalverlauf. Der Hinweis sagt, wo man ist, und führt dorthin.
        */}
        <div className="kanalherkunft">
          <p className="kanalherkunft__text">
            Aus dem Kanal <strong>{kanalName}</strong> — nur die Beiträge zu diesem Punkt.
          </p>
          <button
            className="knopf knopf--schlicht"
            onClick={() => oeffne("kanal", { kanalId: kanalDesTop })}
          >
            Ganzen Kanal öffnen
          </button>
        </div>

        <Fraktionschat
          kanalId={kanalDesTop}
          top={top}
          annotationId={zitatEntwurf}
          aufZitatVerbraucht={() => setzeZitatEntwurf(null)}
          aufTextstelle={zurTextstelle}
          eingebettet
        />
      </Tafel>

      <Tafel schluessel="meinungsbild" aktiv={offenerReiter}>
        <Meinungsbild top={top} />
      </Tafel>

      {werkzeugHinweis && (
        <div className="wkhinweis" role="status">
          <p>
            <strong>{werkzeugHinweis.titel}.</strong>{" "}
            {werkzeugHinweis.fertig
              ? werkzeugHinweis.erklaerung
              : `${werkzeugHinweis.erklaerung} Dieses Werkzeug ist geplant und noch nicht gebaut.`}
          </p>
          <button
            className="knopf knopf--zurueckhaltend"
            onClick={() => setzeWerkzeugHinweis(null)}
          >
            Verstanden
          </button>
        </div>
      )}

      {gewaehlt && (
        <Zitatziel
          zitat={gewaehlt.zitat}
          stelle={gewaehlt.stelle}
          tops={punkte}
          aktuellerTop={top}
          privat={gewaehlt.privat}
          eigen={gewaehlt.eigen}
          bereitsZitiert={gewaehlt.bereitsZitiert}
          notizVorhanden={Boolean(gewaehlt.notiz)}
          farbe={gewaehlt.farbe}
          aufWahl={zielGewaehlt}
          aufFarbe={(neu) => {
            aktionen.setzeAnnotationFarbe(gewaehlt.annotationId, neu);
            setzeGewaehlt((b) => (b ? { ...b, farbe: neu } : b));
          }}
          aufEntfernen={markierungEntfernen}
          aufAbbrechen={() => setzeGewaehlt(null)}
        />
      )}

      {notizEntwurf && (
        <Notizfeld
          zitat={notizEntwurf.zitat}
          stelle={notizEntwurf.stelle}
          wert={notizEntwurf.notiz}
          privat={notizEntwurf.privat}
          aufSichern={notizSichern}
          aufEntfernen={notizEntwurf.notiz ? notizEntfernen : null}
          aufAbbrechen={() => setzeNotizEntwurf(null)}
        />
      )}

      <Werkzeugkreis
        offen={werkzeugeOffen}
        aufOffen={setzeWerkzeugeOffen}
        aufWerkzeug={werkzeugGewaehlt}
        aktiverModus={modus}
        versteckt={leisteOffen}
        /*
          Im Reiter „Chat“ klebt das Eingabefeld samt Senden-Knopf am unteren
          Rand — genau dort, wo sonst der Kreis sitzt. Er rückt dann hoch,
          statt zu verschwinden: Ein Werkzeug, das mal da ist und mal nicht,
          sucht man beim nächsten Mal.
        */
        hoch={offenerReiter === "chat"}
      />
    </>
  );
}

/* ---------------------------------------------------------------
   Ohne gewählten Punkt: Was ist bis zur Sitzung zu tun?
   --------------------------------------------------------------- */

function Sitzungsuebersicht({ oeffne, mitKontext }) {
  const { stammdaten, zustand, ich } = useDemo();
  const offen = offenePunkte(zustand, stammdaten, ich.id);
  const ausschuss = stammdaten.sitzungen[stammdaten.ausschussId];
  const gremium = stammdaten.gremien[ausschuss.gremiumId];

  const zumPunkt = (top, reiter) =>
    top ? oeffne("sitzung", { topId: top.id, reiter }) : undefined;

  return (
    <>
      <div className="detailkopf">
        <p className="detailkopf__vorspann">Nächste Sitzung</p>
        <h1 className="detailkopf__titel">{gremium.name}</h1>
        <p className="klein leise" style={{ marginTop: "0.5rem" }}>
          {langesDatum(ausschuss.beginn)}, {uhrzeit(ausschuss.beginn)} · {ausschuss.ort}
        </p>
      </div>

      <Abschnitt
        titel="Was bis dahin zu tun ist"
        hinweis={`Stand für ${ich.name}. Die Liste wird kürzer, sobald du etwas erledigst.`}
      >
        <ul className="punkte">
          <Punkt
            symbol={Dokument}
            erledigt={offen.ungelesen.length === 0}
            onClick={() => zumPunkt(offen.ungelesen[0], "vorlage")}
          >
            {offen.ungelesen.length === 0 ? (
              <>Alle Vorlagen gelesen.</>
            ) : (
              <>
                <strong>
                  {offen.ungelesen.length === 1
                    ? "Eine Vorlage"
                    : `${offen.ungelesen.length} Vorlagen`}
                </strong>{" "}
                hast du noch nicht gelesen — als nächstes TOP {offen.ungelesen[0].nummer}.
              </>
            )}
          </Punkt>

          <Punkt
            symbol={Waage}
            erledigt={offen.ohneMeinung.length === 0}
            onClick={() => zumPunkt(offen.ohneMeinung[0], "meinungsbild")}
          >
            {offen.ohneMeinung.length === 0 ? (
              <>Zu allen Punkten liegt dein Meinungsbild vor.</>
            ) : (
              <>
                Zu{" "}
                <strong>
                  {offen.ohneMeinung.length === 1
                    ? "einem Tagesordnungspunkt"
                    : `${offen.ohneMeinung.length} Tagesordnungspunkten`}
                </strong>{" "}
                fehlt dein Meinungsbild.
              </>
            )}
          </Punkt>

          <Punkt
            symbol={Uhr}
            erledigt={offen.aufgaben.length === 0}
            onClick={() => oeffne("aufgaben", {})}
          >
            {offen.aufgaben.length === 0 ? (
              <>Keine offenen Aufgaben.</>
            ) : (
              <>
                <strong>
                  {offen.aufgaben.length === 1
                    ? "Eine Aufgabe"
                    : `${offen.aufgaben.length} Aufgaben`}
                </strong>{" "}
                offen — die nächste {fristInWorten(offen.aufgaben[0].faellig)}.
              </>
            )}
          </Punkt>

          <Punkt symbol={Kalender} erledigt={!offen.teilnahmeOffen} onClick={() => {}}>
            {offen.teilnahmeOffen ? (
              <>
                Du hast noch <strong>nicht auf die Fraktionssitzung geantwortet</strong> — die
                Frage steht links unten.
              </>
            ) : (
              <>Rückmeldung zur Fraktionssitzung liegt vor.</>
            )}
          </Punkt>
        </ul>
      </Abschnitt>

      {/*
        Steht die Tagesordnung links, wäre sie hier ein zweites Mal — und ein
        Absatz, der erklärt, was man gleich selbst sieht, ist kein Inhalt.
      */}
      {!mitKontext && (
        <Abschnitt titel="Tagesordnung" hinweis="Aus dem Ratsinformationssystem der Verwaltung.">
          <Tagesordnungsliste oeffne={oeffne} />
        </Abschnitt>
      )}
    </>
  );
}

function kuerze(text, laenge = 70) {
  return text.length <= laenge ? text : `${text.slice(0, laenge).trimEnd()} …`;
}
